import { submitForm } from '../utils/form-submit';
import { withProductPicker } from '../utils/product-picker';
import { showToast } from '../utils/toast';

import {
  findMatchingVariantIndex,
  getAttributeOptions,
  getFilteredAttributeOptions,
  isOptionSoldOut,
} from './order-form/variations';

/**
 * Order form: pick a product, then work through its attributes until exactly
 * one variation matches.
 *
 * Every step, its remaining options and the resolved variation are derived from
 * `selectedProductId` + `selectedAttributes` — nothing about the cascade is
 * stored, so there is no second copy of the truth to keep in sync.
 */
export function orderForm(pickerConfig = {}) {
  return withProductPicker(pickerConfig, {
    loading: true,
    configurations: { products: {}, attribute_options: {} },
    selectedAttributes: {},

    get product() {
      return this.configurations.products[this.selectedProductId] ?? null;
    },

    /**
     * One entry per attribute, in order. Every option the attribute has ever
     * been offered in stays on screen — options the earlier choices have ruled
     * out are struck through rather than removed, so the customer can still see
     * what exists. A step only becomes interactive once every earlier attribute
     * has been answered.
     */
    get steps() {
      const product = this.product;

      if (!product) {
        return [];
      }

      return product.attributes.map((attributeId, index) => {
        const earlier = product.attributes.slice(0, index);
        const priorChoices = Object.fromEntries(
          earlier
            .filter((id) => this.selectedAttributes[id] !== undefined)
            .map((id) => [id, this.selectedAttributes[id]]),
        );

        const reachable = new Set(
          getFilteredAttributeOptions(
            attributeId,
            product,
            priorChoices,
            this.configurations.attribute_options,
          ).map((option) => String(option.id)),
        );

        const options = getAttributeOptions(
          attributeId,
          product,
          this.configurations.attribute_options,
        );

        return {
          attributeId,
          label: product.attribute_labels[attributeId],
          description: product.attribute_descriptions[attributeId],
          enabled: earlier.every((id) => this.selectedAttributes[id] !== undefined),
          selectedOptionId: this.selectedAttributes[attributeId] ?? null,
          options: options.map((option) => {
            const soldOut = isOptionSoldOut(product, attributeId, option.id);

            return {
              ...option,
              soldOut,
              unavailable: soldOut || !reachable.has(String(option.id)),
            };
          }),
        };
      });
    },

    get isComplete() {
      const product = this.product;

      return (
        product !== null &&
        product.attributes.every((id) => this.selectedAttributes[id] !== undefined)
      );
    },

    get variantIndex() {
      if (!this.isComplete) {
        return null;
      }

      const index = findMatchingVariantIndex(this.product, this.selectedAttributes);

      return index === -1 ? null : index;
    },

    get variant() {
      return this.variantIndex === null ? null : this.product.variations[this.variantIndex];
    },

    /** Made-to-measure ("maatwerk") variations are quoted by hand. */
    get isCustom() {
      return this.variant?.custom === true;
    },

    get price() {
      const price = this.variant?.price;

      if (price === undefined || price === null) {
        return null;
      }

      return `€ ${price.toLocaleString('nl-NL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })},-`;
    },

    get selectLabel() {
      return window.js_localizations.select_label;
    },

    get soldOutLabel() {
      return window.js_localizations.sold_out_label;
    },

    optionLabel(option) {
      return option.unavailable
        ? `${option.name} - ${this.soldOutLabel}`
        : `${this.selectLabel} ${option.name}`;
    },

    isOptionSelected(step, option) {
      return step.selectedOptionId === String(option.id);
    },

    async init() {
      const params = parseUrlParams();

      try {
        const response = await fetch(window.js_localizations.configurations_url);

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }

        this.configurations = await response.json();
      } catch (error) {
        console.error('Error fetching product configurations:', error);
      } finally {
        this.loading = false;
      }

      this.applyDeepLink(params);
    },

    /**
     * Deep links (`?product_id=&attribute_*=`, used by the product pages and by
     * emails) preselect the product and its configuration.
     *
     * The old implementation had to wait out a timer and dispatch a synthetic
     * click here, because the DOM was the state. Assigning state before the
     * first render removes the race entirely.
     */
    applyDeepLink({ productId, attributes }) {
      if (productId === null || !this.applyDeepLinkedProduct(productId)) {
        return;
      }

      this.selectedAttributes = { ...attributes };
    },

    /** A different product means a different set of attributes. */
    onProductChange() {
      this.selectedAttributes = {};
    },

    /**
     * Changing any attribute invalidates every later one, so they start over
     * rather than being silently carried into an impossible combination.
     */
    toggleOption(attributeId, optionId) {
      const product = this.product;

      if (!product) {
        return;
      }

      const index = product.attributes.indexOf(attributeId);
      const isDeselect = this.selectedAttributes[attributeId] === String(optionId);

      const next = Object.fromEntries(
        product.attributes
          .slice(0, index)
          .filter((id) => this.selectedAttributes[id] !== undefined)
          .map((id) => [id, this.selectedAttributes[id]]),
      );

      if (!isDeselect) {
        next[attributeId] = String(optionId);
      }

      this.selectedAttributes = next;

      if (this.isCustom) {
        showToast(window.js_localizations.custom_config_message, 7500);
      }
    },

    submit() {
      this.loading = true;

      submitForm({
        form: this.$refs.form,
        url: window.js_localizations.order_submission_url,
        extraJson: {
          product_id: this.selectedProductId,
          ...(this.variantIndex !== null ? { variant_index: this.variantIndex } : {}),
          attributes: { ...this.selectedAttributes },
        },
        event: 'purchase',
        errorDuration: -1,
        onSuccess: () => {
          this.$refs.form.reset();
          this.selectProduct(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
      }).finally(() => {
        this.loading = false;
      });
    },
  });
}

/** `?product_id=69&attribute_38=100` → `{ productId: 69, attributes: { 38: '100' } }`. */
function parseUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const productId = urlParams.get('product_id');
  const attributes = {};

  for (const [key, value] of urlParams.entries()) {
    if (key.startsWith('attribute_')) {
      attributes[key.replace('attribute_', '')] = value;
    }
  }

  return {
    productId: productId ? parseInt(productId, 10) : null,
    attributes,
  };
}
